import { useEffect, useRef, KeyboardEvent, useState } from 'react';
import { useUserProfile } from 'src/context/userProfileContext';
import { useTrackEvents } from 'src/context/userProfileContext/hooks';
import { ErrorBoundary } from '../errorBoundary';
import { Content, Drawer } from './userProfile.styles';
import { UserProfileProps } from './userProfile.types';
import { Activities, Header, Highlights, Interests, UserInfo } from './components';

const UserProfile = ({ theme }: UserProfileProps) => {
  const { userUcode, visible, loggedUserUcode, arrivedFrom, onClose } = useUserProfile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasViewAsBar, setHasViewAsBar] = useState(false);
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (visible) {
      const setFocusInSidebar = () => {
        const hotSidebar = document.querySelector('[slot="header"]') as HTMLElement;
        setHasViewAsBar(hotSidebar?.offsetTop > 0);
        drawerRef?.current?.focus();
      };

      setFocusInSidebar();

      if (arrivedFrom) {
        sendOpenProfileEvent({ componentName: arrivedFrom });
      }
    }
  }, [visible, arrivedFrom, userUcode]);

  useEffect(() => {
    const resetContentScroll = () => {
      contentRef?.current?.scrollTo(0, 0);
    };

    resetContentScroll();
  }, [userUcode, visible]);

  return (
    <ErrorBoundary>
      <Drawer
        ref={drawerRef}
        data-test="drawer"
        className="_position-fixed _shadow"
        theme={theme}
        visible={visible}
        hasViewAsBar={hasViewAsBar}
        role="complementary"
        tabIndex={-1}
        aria-hidden={!visible}
        onKeyDown={handleKeyDown}
      >
        <Header />
        <UserInfo />

        <Content className="_position-relative _overflow-auto">
          <Interests />
          <Highlights />
          <Activities theme={theme} />
        </Content>
      </Drawer>
    </ErrorBoundary>
  );
};

export default UserProfile;
